'use client';

import React, { useState } from 'react';
import { deleteUserPost } from '@/actions/user_specific';
import { useAuthUserContext } from '@/contexts/authUserContext';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
import { NewsCardObject } from '@/lib/types';

interface DeleteButtonProps {
  // Only what the ownership check and delete call need (see ShareBtn.tsx for why).
  card: Pick<NewsCardObject, 'id'> & { data: Pick<NewsCardObject['data'], 'uploaded_by_id'> };
  onDeleteSuccess?: () => void;
}

const DeleteButton: React.FC<DeleteButtonProps> = ({
  card,
  onDeleteSuccess,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { user } = useAuthUserContext();
  const router = useRouter();

  if(user?.uid!==card.data.uploaded_by_id){
    return null;
  }

  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!user?.uid) {
      console.error('User not authenticated');
      return;
    }

    setIsDeleting(true);
    try {
      // Get Firebase ID token
      const userToken = await user.getIdToken();
      await deleteUserPost(card.id, user.uid, userToken);
      setShowConfirmDialog(false);

      // Call the success callback if provided
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }

      router.push(`https://public.app/user/profile/${user.uid}`);

      // Optionally show a success message or redirect
      console.log('Post deleted successfully');
    } catch (error) {
      console.error('Failed to delete post:', error);
      // You might want to show an error toast here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <button
        onClick={handleDeleteClick}
        disabled={isDeleting}
        className='flex items-center space-x-1 text-gray-600 hover:text-red-600 transition-colors disabled:opacity-50'
        aria-label='Delete post'
      >
        <TrashIcon className='w-6 h-6' />
      </button>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Delete Post
            </h3>
            <p className='text-gray-600 mb-6'>
              Are you sure you want to delete this post? This action cannot be
              undone.
            </p>
            <div className='flex space-x-3'>
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className='flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className='flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50'
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteButton;
